import { Band, BandStack } from '@/components/ui/Band'
import { ButtonLink } from '@/components/ui/Button'
import { ArrowLink } from '@/components/ui/ArrowLink'
import { StatePill } from '@/components/ui/StatePill'
import { Icon, IconBadge, type IconName } from '@/components/ui/Icons'
import { Accordion } from '@/components/ui/Accordion'
import { SourceRef } from '@/components/ui/Section'
import { BigHeading, FloatingCard, Marquee, TypedWord } from '@/components/ui/Hero'
import { Reveal, Stagger } from '@/components/motion/Reveal'
import { SplitText, Wipe } from '@/components/motion/SplitText'
import { useTilt, useParallax } from '@/hooks/usePointerFx'
import { useDocumentMeta } from '@/lib/seo'
import { PARTNER_CONTACT_PATH, ROUTES } from '@/lib/site'
import { HOME_FAQ } from '@/content/faq'
import {
  ACCREDITATIONS,
  CATALOGUE_PANELS,
  CLAIM_TAGS,
  EXPORT_SHAPES,
  FORECAST_TIERS,
  PIPELINE_STAGES,
  PRODUCT_SECTIONS,
  READINESS,
  TRACE_STAGES,
} from '@/content/shared'

const TOTAL_LAYERS = 14

const MARQUEE_ITEMS = [
  'HEADER ON ROW 7',
  'BRACKETED NEGATIVES (1,234)',
  'LAKH GROUPING 1,23,456',
  'SUBTOTALS MID-SHEET',
  'SCALE NOTE IN A TITLE CELL',
  'MERGED CELLS',
  'FISCAL YEAR APR–MAR',
  'MULTIPLE TABLES PER SHEET',
]

function PanelPreview() {
  const tilt = useTilt<HTMLDivElement>(5)
  const drift = useParallax<HTMLDivElement>(0.05)

  return (
    <div ref={drift}>
      <FloatingCard ref={tilt} className="card-spot w-full p-6 sm:p-7">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-[15px] font-bold tracking-[-0.01em]">
            One panel, as the product renders it
          </h2>
          <StatePill>illustrative</StatePill>
        </div>

        <div className="mt-5 rounded-card border border-line bg-surface-2/80 p-5">
          <div className="flex items-start justify-between gap-4">
            <p className="text-[13px] font-semibold text-muted">Inventory cover</p>
            <span aria-hidden="true" className="relative h-6 w-6 flex-none">
              <span className="anim-pulse-ring absolute inset-0 rounded-full bg-primary/25" />
              <span className="absolute inset-0 rounded-full bg-primary/15" />
            </span>
          </div>
          <p className="mt-3 flex flex-wrap items-center gap-3">
            <span className="text-[34px] font-extrabold leading-none tracking-[-0.03em] text-ink">
              — days
            </span>
            <StatePill>awaiting your data</StatePill>
          </p>
          <p className="mt-3 text-[13px] leading-[1.7] text-muted">
            Closing inventory over cost of goods sold, in days of the period. The one supply-chain
            panel a trial balance genuinely supports.
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-line pt-3">
            <code className="font-mono text-[11px] text-primary">supply.inventory_cover</code>
            <p className="font-mono text-[11px] text-muted-soft">
              projection: <strong className="font-semibold text-muted">range, 3 qtrs</strong>
            </p>
          </div>
        </div>

        <ul className="mt-5 grid gap-2.5">
          {[
            {
              dot: 'bg-recorded',
              term: 'Recorded',
              detail: '— arithmetic on your documents, traceable to the cell.',
            },
            {
              dot: 'bg-projected',
              term: 'Projected',
              detail: '— a band, with the method that produced it named.',
            },
            {
              dot: 'border border-edge-soft bg-transparent',
              term: 'Blank',
              detail: '— no source document, so no number. Not an estimate.',
            },
          ].map((row) => (
            <li key={row.term} className="flex items-start gap-2.5 text-[13px] leading-[1.65]">
              <span
                aria-hidden="true"
                className={`mt-1.5 h-2 w-2 flex-none rounded-full ${row.dot}`}
              />
              <span className="text-muted">
                <strong className="font-bold text-ink">{row.term}</strong> {row.detail}
              </span>
            </li>
          ))}
        </ul>
      </FloatingCard>
    </div>
  )
}

/** Numbered card used by the pipeline and ladder rails. */
function RailCard({
  id,
  name,
  detail,
  icon,
}: {
  id: string
  name: string
  detail: string
  icon: IconName
}) {
  return (
    <div className="card-spot group flex h-full gap-4 rounded-card border border-line bg-white/85 p-5 shadow-card backdrop-blur">
      <IconBadge name={icon} />
      <span className="min-w-0">
        <span className="font-mono text-[10.5px] text-primary transition-colors group-hover:text-violet">
          {id}
        </span>
        <strong className="mt-1 block text-[14.5px] font-bold tracking-[-0.01em]">{name}</strong>
        <span className="mt-1.5 block text-[13.5px] leading-[1.65] text-muted">{detail}</span>
      </span>
    </div>
  )
}

export default function Home() {
  useDocumentMeta(ROUTES.home)

  return (
    <BandStack>
      {/* 01 — Hero */}
      <Band layer={1} total={TOTAL_LAYERS} tone="ice" routeTab="home">
        <div className="grid items-center gap-16 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:gap-20">
          <div>
            <Reveal variant="fade" duration={600}>
              <p className="eyebrow text-primary">GodEngine · Business Intelligence Simulator</p>
            </Reveal>

            <SplitText
              as="h1"
              text="Every figure, back to the row it was read from."
              highlight={['row']}
              delay={120}
              step={62}
              className="mt-6 block text-balance text-[42px] font-extrabold leading-[0.98] tracking-[-0.035em] sm:text-[60px] lg:text-[76px] xl:text-[84px]"
            />

            <Reveal variant="up" delay={180}>
              <p className="mt-8 max-w-[54ch] text-pretty text-[16px] leading-[1.75] text-muted lg:text-[17.5px]">
                A company hands over its uncurated raw business data. We arrange it, then show two
                things across seven sections —{' '}
                <strong className="font-bold text-ink">recorded</strong>, which is arithmetic on
                documents you supplied, and{' '}
                <strong className="font-bold text-ink">projected</strong>, which is the next three
                quarters as a range.
              </p>
            </Reveal>

            <Reveal variant="up" delay={250}>
              <p className="mt-5 flex flex-wrap items-baseline gap-2 font-mono text-[13px] text-muted">
                <span className="text-muted-soft">what we actually parse:</span>
                <TypedWord
                  className="font-semibold text-primary"
                  words={[
                    'a Tally trial balance',
                    'a Zoho P&L',
                    'a bank statement workbook',
                    'a CRM pipeline export',
                  ]}
                />
              </p>
            </Reveal>

            <Reveal variant="up" delay={320}>
              <div className="mt-10 flex flex-wrap gap-3">
                <ButtonLink to={PARTNER_CONTACT_PATH} size="lg">
                  Become a design partner
                </ButtonLink>
                <ButtonLink to={ROUTES.method.path} variant="secondary" size="lg">
                  Read the method
                </ButtonLink>
              </div>
            </Reveal>

            <Reveal variant="fade" delay={420}>
              <p className="mt-7 font-mono text-[11px] tracking-[0.06em] text-primary">
                We are asking for messy exports, not for a logo on a wall.
              </p>
            </Reveal>
          </div>

          <Reveal variant="scale" delay={260} duration={900}>
            <PanelPreview />
          </Reveal>
        </div>

        <Wipe delay={620} className="mt-16">
          <Marquee items={MARQUEE_ITEMS} />
        </Wipe>
      </Band>

      {/* 02 — The rule */}
      <Band layer={2} total={TOTAL_LAYERS} tone="warm" labelledBy="rule-heading">
        <div className="grid gap-14 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-20">
          <div>
            <BigHeading id="rule-heading" eyebrow="01 — The rule everything else follows from">
              A panel with no source document behind it shows nothing.
            </BigHeading>
            <Reveal variant="up" delay={200}>
              <p className="mt-8 max-w-[62ch] text-[15.5px] leading-[1.8] text-muted">
                Filling that gap with an industry average or a modelled guess would look identical
                to a real reading, and there would be no way to tell them apart later. A dashboard
                that is 80% real and 20% plausible is not 80% useful. It is unusable, because you
                cannot tell which fifth is which.
              </p>
            </Reveal>
            <Reveal variant="up" delay={280}>
              <p className="mt-5 max-w-[62ch] text-[15.5px] leading-[1.8] text-muted">
                So a locked panel says what document would unlock it, and stays empty until that
                document arrives.
              </p>
            </Reveal>
          </div>

          <Stagger variant="left" step={110} className="grid content-center gap-4" itemClassName="">
            {[
              {
                label: 'RAW EXPORT',
                icon: 'parse' as const,
                accent: 'from-[#f0776a] to-[#f7b3aa]',
                width: 'w-[96%]',
                rows: [
                  ['Row 1–6', 'title block'],
                  ['Closing stock', '(1,23,456)'],
                  ['Figures in', '₹ lakh'],
                ],
              },
              {
                label: 'NORMALISED',
                icon: 'normalize' as const,
                accent: 'from-[#f0b429] to-[#f7d894]',
                width: 'w-[86%]',
                rows: [
                  ['Header row', '7'],
                  ['Closing stock', '−12,345,600'],
                  ['Unit', 'INR'],
                ],
              },
              {
                label: 'TRACEABLE PANEL',
                icon: 'recorded' as const,
                accent: 'from-[#12946b] to-[#7fd8ba]',
                width: 'w-[76%]',
                rows: [
                  ['Formula', 'stock ÷ cogs × days'],
                  ['Source', 'sheet 1, row 42'],
                ],
              },
              {
                label: 'PROJECTED RANGE',
                icon: 'projection' as const,
                accent: 'from-primary to-[#93a8f5]',
                width: 'w-[66%]',
                rows: [
                  ['Shape', 'p10 · p50 · p90'],
                  ['Decoder', 'named on panel'],
                ],
              },
            ].map((card) => (
              <div
                key={card.label}
                className={`card-spot group ml-auto ${card.width} rounded-card border border-line bg-white/85 p-5 shadow-card backdrop-blur`}
              >
                <div className="flex items-center gap-2.5">
                  <Icon name={card.icon} className="h-4 w-4 flex-none text-muted-soft" />
                  <p className="font-mono text-[10px] tracking-[0.16em] text-muted">{card.label}</p>
                </div>
                <span
                  className={`mt-3 block h-1.5 w-full rounded-full bg-gradient-to-r ${card.accent}`}
                />
                {/* Illustrative notation, matching the shapes named in the copy. */}
                <dl className="mt-3.5 grid gap-1.5">
                  {card.rows.map(([term, value]) => (
                    <div key={term} className="flex items-baseline justify-between gap-4">
                      <dt className="text-[11.5px] text-muted-soft">{term}</dt>
                      <dd className="font-mono text-[11.5px] font-medium text-ink">{value}</dd>
                    </div>
                  ))}
                </dl>
                {/* The rest of the sheet, left as unread rows. */}
                <span className="mt-3 block h-1.5 w-1/2 rounded-full bg-line" />
                <span className="mt-1.5 block h-1.5 w-1/2 rounded-full bg-line" />
              </div>
            ))}
          </Stagger>
        </div>

        <Reveal variant="up" delay={120} className="mt-14">
          <div className="grid gap-6 rounded-card border border-line bg-white/70 p-7 backdrop-blur sm:grid-cols-[1fr_auto] sm:items-center">
            <p className="max-w-[62ch] text-[15px] leading-[1.75] text-muted">
              Seven KPIs are computable from an accounting export alone. The rest of the catalogue
              names the document it needs.
            </p>
            <SourceRef>bi/compute/kpis.py · computable_ids()</SourceRef>
          </div>
        </Reveal>
      </Band>

      {/* 03 — Two kinds of number */}
      <Band layer={3} total={TOTAL_LAYERS} tone="ice" labelledBy="kinds-heading">
        <BigHeading
          id="kinds-heading"
          eyebrow="02 — Two kinds of number, never mixed"
          align="center"
        >
          The whole product is built on one distinction.
        </BigHeading>

        <Stagger variant="up" step={140} className="mt-14 grid gap-6 lg:grid-cols-2">
          {[
            {
              tag: 'Recorded · your books',
              title: 'Arithmetic, and nothing else',
              body: 'A recorded figure is a formula over measures extracted from documents you supplied. It carries the formula, the measures, the source file and the row. If you disagree with it, you can open it and find out where we read it wrong.',
              rows: [
                ['Provenance', 'filed, or derived'],
                ['Carries', 'formula, drivers, line items, sources'],
                ['Colour', '#3b5bdb everywhere, without exception'],
              ],
              accent: 'text-recorded',
              ring: 'hover:border-recorded/30',
              icon: 'recorded' as IconName,
              tone: 'primary' as const,
            },
            {
              tag: 'Projected · modelled',
              title: 'A band, and the method that made it',
              body: 'Three quarters forward, as p10 / p50 / p90. Every projection names its tier, its decoder, how many points it trained on, how many it held back, and its fit on that holdout. When there is not enough history, the panel locks instead of guessing.',
              rows: [
                ['Shape', 'p10 · p50 · p90, per period'],
                ['Carries', 'tier, decoder, train / holdout points, MAE'],
                ['Colour', '#e8952c, hatched where it is a band'],
              ],
              accent: 'text-projected',
              ring: 'hover:border-projected/30',
              icon: 'projection' as IconName,
              tone: 'projected' as const,
            },
          ].map((card) => (
            <article
              key={card.title}
              className={`card-spot h-full rounded-card border border-line bg-white/85 p-7 shadow-card backdrop-blur sm:p-9 ${card.ring}`}
            >
              <div className="flex items-center gap-4">
                <IconBadge name={card.icon} tone={card.tone} />
                <p className={`font-mono text-[11px] tracking-[0.1em] ${card.accent}`}>
                  {card.tag}
                </p>
              </div>
              <h3 className="mt-5 text-[24px] font-extrabold leading-tight tracking-[-0.02em]">
                {card.title}
              </h3>
              <p className="mt-5 text-[15px] leading-[1.8] text-muted">{card.body}</p>
              <dl className="mt-7 grid gap-3 border-t border-line pt-6">
                {card.rows.map(([term, detail]) => (
                  <div key={term} className="grid gap-1 sm:grid-cols-[140px_1fr] sm:gap-4">
                    <dt className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-muted-soft">
                      {term}
                    </dt>
                    <dd className="text-[13.5px] leading-[1.6] text-muted">{detail}</dd>
                  </div>
                ))}
              </dl>
            </article>
          ))}
        </Stagger>

        <Reveal variant="fade" delay={200}>
          <p className="mx-auto mt-10 max-w-[70ch] text-center text-[15px] leading-[1.8] text-muted">
            Charts, legends, pills and bars all derive from that one distinction, which is why the
            design tokens are named{' '}
            <code className="font-mono text-[13.5px] text-recorded">recorded</code> and{' '}
            <code className="font-mono text-[13.5px] text-projected">projected</code> rather than
            for their hue.
          </p>
        </Reveal>
      </Band>

      {/* 04 — Surfaces */}
      <Band layer={4} total={TOTAL_LAYERS} tone="mint" labelledBy="surfaces-heading">
        <BigHeading
          id="surfaces-heading"
          eyebrow="03 — Surfaces"
          action={<ArrowLink to={ROUTES.sections.path}>Every panel and what unlocks it</ArrowLink>}
        >
          Seven sections, four groups.
        </BigHeading>

        <Stagger
          variant="up"
          step={80}
          as="ul"
          itemAs="li"
          className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        >
          {PRODUCT_SECTIONS.map((section) => (
            <div
              key={section.slug}
              className="card-spot group flex h-full flex-col rounded-card border border-line bg-white/85 p-6 shadow-card backdrop-blur"
            >
              <IconBadge
                name={section.icon}
                tone={
                  section.group === 'Core'
                    ? 'primary'
                    : section.group === 'Delivery'
                      ? 'violet'
                      : section.group === 'Governance'
                        ? 'computes'
                        : 'projected'
                }
              />
              <p className="mt-4 font-mono text-[10.5px] uppercase tracking-[0.14em] text-muted-soft">
                {section.group}
              </p>
              <h3 className="mt-2 text-[19px] font-extrabold tracking-[-0.02em]">{section.name}</h3>
              <p className="mt-3 text-[13.5px] leading-[1.7] text-muted">{section.detail}</p>
              <span
                aria-hidden="true"
                className="mt-5 block h-[2px] w-10 origin-left rounded-full bg-gradient-to-r from-primary to-violet transition-transform duration-300 ease-brand group-hover:scale-x-[2.4]"
              />
            </div>
          ))}
        </Stagger>

        <Reveal variant="up" delay={160} className="mt-8">
          <div className="grid gap-6 rounded-card border border-line bg-white/70 p-7 backdrop-blur sm:grid-cols-[1fr_auto] sm:items-center">
            <p className="max-w-[70ch] text-[15px] leading-[1.75] text-muted">
              Section depth follows data availability. Finance is safe. Supply chain needs a
              purchase-order register and a goods-receipt log; Compliance needs a filing calendar.
            </p>
            <SourceRef>bi/compute/kpis.py · SECTION_GROUPS</SourceRef>
          </div>
        </Reveal>
      </Band>

      {/* 05 — The data plane */}
      <Band layer={5} total={TOTAL_LAYERS} tone="ice" labelledBy="plane-heading">
        <BigHeading
          id="plane-heading"
          eyebrow="04 — The data plane"
          lede="Nothing is thrown away between them. Each stage records what it did, so the trace on any figure can walk back through all eight and show you the cell."
          action={<ArrowLink to={ROUTES.method.path}>Walk the pipeline</ArrowLink>}
        >
          Eight stages between a messy workbook and a panel.
        </BigHeading>

        <Stagger
          variant="up"
          step={70}
          as="ol"
          itemAs="li"
          className="mt-14 grid gap-4 sm:grid-cols-2 xl:grid-cols-4"
        >
          {PIPELINE_STAGES.map((stage) => (
            <RailCard
              key={stage.id}
              id={stage.id}
              name={stage.name}
              detail={stage.detail}
              icon={stage.icon}
            />
          ))}
        </Stagger>

        <Reveal variant="fade" delay={200}>
          <p className="mt-10 max-w-[76ch] text-[15px] leading-[1.8] text-muted">
            Accounting-grade extraction is ours, deliberately. The engine&rsquo;s multimodal
            ingester is right for classification and for non-tabular documents — it caps sheets and
            rows and keeps numeric cells only, which is correct for a digest and fatal for a general
            ledger.
          </p>
        </Reveal>
      </Band>

      {/* 06 — Locked panels */}
      <Band layer={6} total={TOTAL_LAYERS} tone="warm" labelledBy="locked-heading">
        <BigHeading
          id="locked-heading"
          eyebrow="05 — What a locked panel says"
          lede="Each panel that cannot be computed states why, and names the document that would unlock it. Send that document and the panel fills; send nothing and it stays honest."
        >
          An empty panel is a request, not a failure.
        </BigHeading>

        <Reveal variant="up" delay={160} className="mt-14">
          <div className="overflow-hidden rounded-card border border-line bg-white/85 shadow-card backdrop-blur">
            <div className="scroll-x">
              <table className="w-full min-w-[760px] border-collapse text-left">
                <caption className="px-7 pt-7 text-left text-[13px] text-muted-soft">
                  Selected panels from the catalogue, with their state on a trial balance alone.
                </caption>
                <thead>
                  <tr className="border-b border-line">
                    {['Panel', 'State', 'Why, or what unlocks it'].map((heading) => (
                      <th
                        key={heading}
                        scope="col"
                        className="px-7 py-4 font-mono text-[10.5px] uppercase tracking-[0.14em] text-primary"
                      >
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {CATALOGUE_PANELS.map((panel) => (
                    <tr
                      key={panel.id}
                      className="border-b border-line transition-colors last:border-b-0 hover:bg-primary/[0.03]"
                    >
                      <td className="px-7 py-5 align-top">
                        <strong className="block text-[14px] font-bold">{panel.name}</strong>
                        <code className="mt-1 block font-mono text-[11px] text-primary">
                          {panel.id}
                        </code>
                      </td>
                      <td className="px-7 py-5 align-top">
                        <StatePill tone={panel.state === 'computes' ? 'computes' : 'locked'}>
                          {panel.state}
                        </StatePill>
                      </td>
                      <td className="px-7 py-5 align-top text-[13.5px] leading-[1.7] text-muted">
                        {panel.reason}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Reveal>

        <div className="mt-5">
          <SourceRef>
            Copy in this table is the product&rsquo;s own not-computable text · bi/compute/kpis.py
          </SourceRef>
        </div>
      </Band>

      {/* 07 — Forecast ladder */}
      <Band layer={7} total={TOTAL_LAYERS} tone="ice" labelledBy="ladder-heading">
        <BigHeading
          id="ladder-heading"
          eyebrow="06 — The forecast ladder"
          lede="Six tiers, tried in order. The panel is labelled with the one that actually produced it, and a projection is never labelled TimesFM unless it came from TimesFM."
          action={
            <ArrowLink to={ROUTES.projection.path}>How a projection is built and checked</ArrowLink>
          }
        >
          The method is part of the number.
        </BigHeading>

        <Stagger
          variant="up"
          step={80}
          as="ol"
          itemAs="li"
          className="mt-14 grid gap-4 sm:grid-cols-2 xl:grid-cols-3"
        >
          {FORECAST_TIERS.map((tier) => (
            <RailCard
              key={tier.id}
              id={tier.id}
              name={tier.name}
              detail={tier.detail}
              icon={tier.icon}
            />
          ))}
        </Stagger>

        <Reveal variant="fade" delay={200}>
          <p className="mt-10 max-w-[76ch] text-[15px] leading-[1.8] text-muted">
            With the model weights absent, the loader returns a stub. The gate checks the decoder
            name rather than trusting the call, so a stub result falls through to classical models
            instead of being served as a foundation-model forecast.
          </p>
        </Reveal>
      </Band>

      {/* 08 — Trace */}
      <Band layer={8} total={TOTAL_LAYERS} tone="mint" labelledBy="trace-heading">
        <BigHeading
          id="trace-heading"
          eyebrow="07 — Trace"
          lede="Not a citation appended to a claim. A record of the operation, in order, each stage carrying its own rows and the source id behind each one."
        >
          Open any figure and walk it back five stages.
        </BigHeading>

        <Stagger
          variant="up"
          step={90}
          as="ol"
          itemAs="li"
          className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-5"
        >
          {TRACE_STAGES.map((stage, index) => (
            <div
              key={stage.name}
              className="card-spot h-full rounded-card border border-line bg-white/85 p-6 shadow-card backdrop-blur"
            >
              <IconBadge
                name={stage.icon}
                tone={
                  stage.kind === 'Recorded'
                    ? 'primary'
                    : stage.kind === 'Projected'
                      ? 'projected'
                      : 'violet'
                }
              />
              <p className="mt-4 font-mono text-[10.5px] uppercase tracking-[0.14em] text-muted-soft">
                <span className="sr-only">Stage {index + 1}: </span>
                {stage.kind}
              </p>
              <h3 className="mt-2 text-[17px] font-extrabold tracking-[-0.02em]">{stage.name}</h3>
              <p className="mt-3 text-[13px] leading-[1.7] text-muted">{stage.detail}</p>
            </div>
          ))}
        </Stagger>

        <Reveal variant="up" delay={200} className="mt-10">
          <div className="rounded-card border border-line bg-white/75 p-7 backdrop-blur sm:p-9">
            <p className="max-w-[76ch] text-[15px] leading-[1.8] text-muted">
              <strong className="font-bold text-ink">Answers are checked the same way.</strong>{' '}
              Before a written answer is shown, every number in it is verified against the grounding
              it was built from. The check is substitution-aware — it catches a figure drifting, not
              just a missing citation. On failure the pipeline regenerates once, then abstains.
            </p>
            <p className="mt-5 max-w-[76ch] text-[15px] leading-[1.8] text-muted">
              Abstaining is a supported outcome. It is the only alternative to a confident wrong
              number, and the product prefers it.
            </p>
            <SourceRef>bi/engine_bridge.py · check_figures()</SourceRef>
          </div>
        </Reveal>
      </Band>

      {/* 09 — Real exports */}
      <Band layer={9} total={TOTAL_LAYERS} tone="warm" labelledBy="exports-heading">
        <BigHeading
          id="exports-heading"
          eyebrow="08 — What real exports look like"
          lede="These shapes cannot be invented convincingly. Each one written from imagination is a test that passes for the wrong reason, so we ask for the real thing."
        >
          Nobody sends a clean table.
        </BigHeading>

        <Stagger
          variant="up"
          step={80}
          as="ul"
          itemAs="li"
          className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
        >
          {EXPORT_SHAPES.map((shape) => (
            <div
              key={shape.name}
              className="card-spot h-full rounded-card border border-line bg-white/85 p-7 shadow-card backdrop-blur"
            >
              <div className="flex items-center justify-between gap-3">
                <IconBadge name={shape.icon} tone="projected" />
                <p className="font-mono text-[13px] text-projected">{shape.sample}</p>
              </div>
              <h3 className="mt-4 text-[18px] font-extrabold tracking-[-0.02em]">{shape.name}</h3>
              <p className="mt-3 text-[13.5px] leading-[1.7] text-muted">{shape.detail}</p>
            </div>
          ))}
          <div className="anim-sweep relative flex h-full flex-col justify-between overflow-hidden rounded-card border border-primary/30 bg-primary/[0.05] p-7">
            <div>
              <IconBadge name="send" />
              <h3 className="mt-4 text-[18px] font-extrabold tracking-[-0.02em]">Send us yours</h3>
              <p className="mt-3 text-[13.5px] leading-[1.7] text-muted">
                A Tally trial balance, a Zoho or Busy P&amp;L, a bank statement workbook, a CRM
                pipeline export. Anonymised is fine — the shape is what matters, not the values.
              </p>
            </div>
            <div className="relative mt-6">
              <ArrowLink to={PARTNER_CONTACT_PATH}>Send an export</ArrowLink>
            </div>
          </div>
        </Stagger>
      </Band>

      {/* 10 — Claim tags */}
      <Band layer={10} total={TOTAL_LAYERS} tone="ice" labelledBy="claims-heading">
        <div className="grid gap-14 lg:grid-cols-2 lg:gap-20">
          <div>
            <BigHeading id="claims-heading" eyebrow="09 — How we talk about ourselves">
              Every claim we make carries a tag.
            </BigHeading>
            <Reveal variant="up" delay={200}>
              <p className="mt-8 max-w-[62ch] text-[15.5px] leading-[1.8] text-muted">
                The same discipline the product applies to your figures, applied to our own
                statements. An untagged claim is a defect. A number with no tag does not ship.
              </p>
            </Reveal>
            <Reveal variant="up" delay={270}>
              <p className="mt-5 max-w-[62ch] text-[15.5px] leading-[1.8] text-muted">
                Which is why this website has no customer count, no revenue figure, no testimonial
                and no logo wall. We do not have them yet, and a plausible-looking placeholder is
                the one thing we will not write.
              </p>
            </Reveal>
          </div>

          <Stagger variant="right" step={90} as="dl" className="grid content-center gap-3">
            {CLAIM_TAGS.map((claim) => (
              <div
                key={claim.tag}
                className="card-spot grid gap-1 rounded-card border border-line bg-white/85 p-5 shadow-card backdrop-blur sm:grid-cols-[190px_1fr] sm:items-baseline sm:gap-4"
              >
                <dt className="font-mono text-[12px] text-primary">{claim.tag}</dt>
                <dd className="text-[13.5px] leading-[1.65] text-muted">{claim.meaning}</dd>
              </div>
            ))}
          </Stagger>
        </div>
      </Band>

      {/* 11 — Third-party validation */}
      <Band layer={11} total={TOTAL_LAYERS} tone="mint" labelledBy="validation-heading">
        <BigHeading id="validation-heading" eyebrow="10 — Third-party validation, all of it">
          Three programmes. That is the complete list.
        </BigHeading>

        <Stagger
          variant="up"
          step={110}
          as="ul"
          itemAs="li"
          className="mt-14 grid gap-5 sm:grid-cols-3"
        >
          {ACCREDITATIONS.map((item) => (
            <div
              key={item.name}
              className="card-spot h-full rounded-card border border-line bg-white/85 p-7 shadow-card backdrop-blur"
            >
              <h3 className="text-[18px] font-extrabold tracking-[-0.02em]">{item.name}</h3>
              <p className="mt-3 text-[13.5px] leading-[1.7] text-muted">{item.detail}</p>
              <p className="mt-5 font-mono text-[11px] text-muted-soft">{item.tag}</p>
            </div>
          ))}
        </Stagger>

        <Reveal variant="up" delay={180}>
          <div className="mt-10 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
            <p className="max-w-[70ch] text-[15px] leading-[1.8] text-muted">
              Three programmes, not four. An earlier marketing page claimed a fourth acceptance with
              a dollar figure attached; it is unattested, so it does not appear here.
            </p>
            <ArrowLink to={ROUTES.company.path}>More on how the company describes itself</ArrowLink>
          </div>
        </Reveal>
      </Band>

      {/* 12 — Where this is */}
      <Band layer={12} total={TOTAL_LAYERS} tone="ink" labelledBy="phase-heading" showMark={false}>
        <div className="grid gap-14 lg:grid-cols-2 lg:gap-20">
          <div>
            <Reveal variant="fade">
              <p className="eyebrow text-white/60">11 — Where this actually is</p>
            </Reveal>
            <Reveal variant="up" delay={90}>
              <h2
                id="phase-heading"
                className="mt-5 text-balance text-[28px] font-extrabold leading-[1.12] tracking-[-0.03em] text-white sm:text-[38px] lg:text-[44px]"
              >
                Phase 0 is complete. That is a smaller claim than it sounds, and we would rather
                state it plainly.
              </h2>
            </Reveal>
            <Reveal variant="up" delay={180}>
              <p className="mt-7 max-w-[62ch] text-[15.5px] leading-[1.8] text-white/70">
                The repository boots, the vendored engine is reachable across all eight bridged
                capabilities, the first migration applies and reverses, the frontend builds, and
                every CI gate is green and blocking.
              </p>
            </Reveal>
            <Reveal variant="up" delay={250}>
              <p className="mt-5 max-w-[62ch] text-[15.5px] leading-[1.8] text-white/70">
                The landing surface renders the live readiness payload, so opening it proves the
                whole chain rather than showing a shell — and tells you which link is broken when
                one is.
              </p>
            </Reveal>
          </div>

          <div>
            <Stagger
              variant="left"
              step={70}
              as="ul"
              itemAs="li"
              className="grid gap-2 rounded-card border border-white/15 bg-white/[0.07] p-6 backdrop-blur"
            >
              {READINESS.map((item) => (
                <div
                  key={item.label}
                  className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 py-3.5 last:border-b-0"
                >
                  <span className="text-[14px] font-semibold text-white">{item.label}</span>
                  <span
                    className={`inline-flex items-center rounded-control border px-2.5 py-1 font-mono text-[10.5px] ${
                      item.done
                        ? 'border-computes/40 bg-computes/20 text-[#8ff0c8]'
                        : 'border-white/20 bg-white/10 text-white/70'
                    }`}
                  >
                    {item.state}
                  </span>
                </div>
              ))}
            </Stagger>
            <Reveal variant="fade" delay={300}>
              <p className="mt-6 max-w-[62ch] text-[13.5px] leading-[1.7] text-white/60">
                Nothing can serve a projection by accident: the projection, temporal, ensemble and
                Ask flags all default off.
              </p>
            </Reveal>
          </div>
        </div>
      </Band>

      {/* 13 — Questions */}
      <Band layer={13} total={TOTAL_LAYERS} tone="warm" labelledBy="questions-heading" narrow>
        <BigHeading
          id="questions-heading"
          eyebrow="12 — Questions"
          align="center"
          action={undefined}
        >
          The ones that get asked first.
        </BigHeading>
        <Reveal variant="up" delay={140} className="mt-12">
          <Accordion items={HOME_FAQ} />
        </Reveal>
        <Reveal variant="fade" delay={200} className="mt-8 flex justify-center">
          <ArrowLink to={ROUTES.faq.path}>All questions</ArrowLink>
        </Reveal>
      </Band>

      {/* 14 — Closing CTA */}
      <Band layer={14} total={TOTAL_LAYERS} tone="ice" labelledBy="cta-heading" showMark={false}>
        <div className="mx-auto max-w-4xl text-center">
          <Reveal variant="fade">
            <p className="eyebrow text-primary">Design partners</p>
          </Reveal>
          <Reveal variant="up" delay={90}>
            <h2
              id="cta-heading"
              className="mt-6 text-balance text-[30px] font-extrabold leading-[1.1] tracking-[-0.03em] sm:text-[42px] lg:text-[50px]"
            >
              Three to five real, messy exports would change this product more than any feature.
            </h2>
          </Reveal>
          <Reveal variant="up" delay={180}>
            <p className="mx-auto mt-7 max-w-[62ch] text-pretty text-[16px] leading-[1.8] text-muted">
              Anonymised is fine. We want the shapes — the header on row seven, the mid-sheet
              subtotals, the scale note buried in a title cell. In exchange you get the first
              working sections built against your own data.
            </p>
          </Reveal>
          <Reveal variant="up" delay={260}>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <ButtonLink to={PARTNER_CONTACT_PATH} size="lg">
                Become a design partner
              </ButtonLink>
              <ButtonLink to={ROUTES.method.path} variant="secondary" size="lg">
                Read the method first
              </ButtonLink>
            </div>
          </Reveal>
        </div>
      </Band>
    </BandStack>
  )
}
